import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { th as palette, Btn } from "@/lib/bricks";
import { Page, PageHeader, Stats, Empty, Loading, TextLink } from "@/components/calm/Calm";
import DealImport from "@/components/dealimport/DealImport";
import { importAction, importStats, resultMessage, fetchMessage } from "@/components/dealimport/steps";
import { visibleImport } from "@/lib/integrations";

// Import deals from the sources in ENABLED_DEAL_SOURCES (Lone Wolf Back Office). Managers only; hidden unless the office has it switched on.
export default function DealImportPage() {
  const { resolvedTheme } = useTheme();
  const { session } = useAuth();
  const dark = resolvedTheme === "dark";
  const p = palette(dark);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | off
  const [working, setWorking] = useState(false);
  const [matchOpen, setMatchOpen] = useState(false);
  const [connectFor, setConnectFor] = useState(null);

  const BASE = process.env.REACT_APP_BACKEND_URL;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token}` };

  const load = async () => {
    const res = await fetch(`${BASE}/api/deal-import`, { headers }).catch(() => null);
    if (!res || !res.ok) { setState("off"); return; }
    setData(visibleImport(await res.json().catch(() => null)));
    setState("ready");
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const call = async (method, path, body) => {
    const res = await fetch(`${BASE}/api/deal-import/${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined }).catch(() => null);
    const d = res ? await res.json().catch(() => ({})) : {};
    if (!res || !res.ok) {
      toast.error(typeof d?.detail?.message === "string" ? d.detail.message : "That didn't work. Try again.");
      if (res && res.status === 409) await load();
      return null;
    }
    setData(visibleImport(d));
    if (path === "connect" && method === "POST") toast.success(`Connected to ${d.connection?.name || "your back office"}`);
    if (path === "fetch") toast.success(fetchMessage(d.found));
    if (path === "agents") toast.success("Agents matched");
    if (path === "run" && d.result) { const m = resultMessage(d.result); (m.ok ? toast.success : toast.error)(m.text); }
    return d;
  };

  const next = data ? importAction(data) : null;
  const doNext = async () => {
    if (!next) return;
    if (next.kind === "match") return setMatchOpen(true);
    if (next.kind === "reconnect") {
      const src = (data.sources || []).find((s) => s.key === data.connection.source);
      if (src && src.fields?.length) return setConnectFor(src.key);
      return call("POST", "connect", { source: data.connection.source });
    }
    setWorking(true);
    try { await call("POST", next.kind === "fetch" ? "fetch" : "run", next.kind === "run" ? {} : undefined); } finally { setWorking(false); }
  };

  if (state === "loading") return <Loading />;
  if (state === "off") {
    return (
      <Page testid="deal-import-page">
        <PageHeader title="Import deals" />
        <Empty action={<TextLink onClick={() => navigate("/transactions")}>Back to transactions</TextLink>}>
          Importing deals isn't switched on for your office.
        </Empty>
      </Page>
    );
  }

  const busyLabel = next?.kind === "fetch" ? "Checking…" : "Importing…";
  return (
    <Page testid="deal-import-page">
      <PageHeader
        title="Import deals"
        subtitle={data?.connection ? `From ${data.connection.name}` : "From Lone Wolf Back Office"}
        action={next ? <Btn variant="primary" dark={dark} data-testid="import-next" style={{ whiteSpace: "nowrap" }} disabled={working} onClick={doNext}>{working ? busyLabel : next.label}</Btn> : null}
      />
      {data?.connection?.fetched_at ? <Stats testid="import-stats" items={importStats(data)} /> : null}
      <DealImport data={data} p={p} dark={dark} call={call}
        matchOpen={matchOpen} setMatchOpen={setMatchOpen} connectFor={connectFor} setConnectFor={setConnectFor} />
      <div style={{ marginTop: 28 }}><TextLink testid="import-back" onClick={() => navigate("/transactions")}>Back to transactions</TextLink></div>
    </Page>
  );
}
